<template>
  <Page class="overflow-x-hidden">
    <template #title>
      <div class="flex flex-row items-center justify-between">
        <div>Реестр ЛМСЗ для информирования</div>
        <LMSPExportBtn />
      </div>
    </template>

    <PageContent>
      <div class="overflow-x-scroll">
        <BaseTable
          class="mb-10"
          :columns="tableColumns"
          :items="requests"
          :is-loading="isLoading"
          @row-click="onRowClick"
        >
          <template #header-id="column">
            {{ column.name }}
            <BaseInput
              class="mt-1"
              id="kmsz_id"
              v-model="filter.id"
              @blur="changeFilter"
              @keyup="applyFilter"
            />
          </template>

          <template #header-status="column">
            {{ column.name }}
            <LazySelect
              class="filter-select mt-1"
              label="name"
              v-model="filter.status"
              :clearable="true"
              :source="loadLMSPRequestStatuses"
              @input="changeFilter"
            />
          </template>

          <template #header-npa_level="column">
            {{ column.name }}
            <LazySelect
              class="filter-select mt-1"
              label="name"
              v-model="filter.level"
              :clearable="true"
              :source="loadLevels"
              @input="changeFilter"
            />
          </template>

          <template #header-life_situation="column">
            {{ column.name }}
            <LazySelect
              class="filter-select mt-1"
              label="name"
              v-model="filter.lifeSituation"
              :clearable="true"
              :source="loadLifeSituationsSelect"
              @input="changeFilter"
            />
          </template>

          <template #header-kmsz_code="column">
            {{ column.name }}
            <BaseInput
              class="mt-1"
              id="kmsz_code"
              v-model="filter.kmszCode"
              @blur="changeFilter"
              @keyup="applyFilter"
            />
          </template>

          <template #header-lmsp_name="column">
            {{ column.name }}
            <BaseInput
              class="mt-1"
              id="lmsp_name"
              v-model="filter.lmspName"
              @blur="changeFilter"
              @keyup="applyFilter"
            />
          </template>

          <template #column-npa_level="item">
            {{ item.npa_code }} {{ item.npa_level }}
          </template>

          <template #header-provider_code="column">
            {{ column.name }}
            <BaseInput
              class="mt-1"
              id="provider_code"
              v-model="filter.providerCode"
              @blur="changeFilter"
              @keyup="applyFilter"
            />
          </template>

          <template #header-provider_region="column">
            {{ column.name }}
            <BaseInput
              class="mt-1"
              id="provider_region"
              v-model="filter.providerRegion"
              @blur="changeFilter"
              @keyup="applyFilter"
            />
          </template>

          <template #header-need_frgu="column">
            {{ column.name }}
            <BaseSelect
              class="mt-1 small-select"
              id="need_frgu"
              :clearable="true"
              :options="needFrguSelect"
              :reduce="(item) => +item.value"
              v-model="filter.needFrgu"
              @input="changeFilter"
            />
          </template>

          <template #column-need_frgu="item">
            <span v-if="item.need_frgu">Да</span>
            <span v-else>Нет</span>
          </template>
        </BaseTable>
      </div>

      <BasePagination
        class="my-6"
        :pagination="pagination"
        @paginate="onPaginate"
      />
    </PageContent>
  </Page>
</template>

<script>
  import {mapGetters, mapState} from 'vuex';

  import isEqual from 'lodash/isEqual';
  import intersection from 'lodash/intersection';

  import Client from '@/classes/Api/Client';

  import Page from '@/components/layouts/main/page/index';
  import LazySelect from '@/components/global/lazy-select';
  import BaseSelect from '@/components/global/base-select';
  import BaseTable from '@/components/global/base-table/index';
  import BaseInput from '@/components/global/base-input/index';
  import LMSPExportBtn from '@/components/views/lmsp/export-btn';
  import PageContent from '@/components/layouts/main/page/PageContent';
  import BasePagination from '@/components/global/base-pagination/index';

  export default {
    name: 'RegistryListPage',

    components: {
      Page,
      BaseInput,
      BaseTable,
      LazySelect,
      BaseSelect,
      PageContent,
      LMSPExportBtn,
      BasePagination,
    },

    data() {
      return {
        isLoading: false,

        filter: {
          id: null,
          level: null,
          status: null,
          kmszCode: null,
          lmspName: null,
          needFrgu: null,
          providerCode: null,
          lifeSituation: null,
          providerRegion: null,
        },

        needFrguSelect: [
          {
            label: 'Да',
            value: true,
          },
        ],

        columns: [
          {
            name: 'Номер',
            slug: 'id',
            classes: ['cursor-pointer'],
            headClasses: ['align-baseline', 'head-id'],
            roles: [],
          },
          {
            name: 'Жизненная ситуация',
            slug: 'life_situation',
            classes: ['cursor-pointer'],
            headClasses: ['align-baseline', 'head-life_situation'],
            roles: [],
          },
          {
            name: 'Уровень НПА',
            slug: 'npa_level',
            classes: ['cursor-pointer'],
            headClasses: ['align-baseline', 'head-npa_level'],
            roles: [],
          },
          {
            name: 'Код КМСЗ',
            slug: 'kmsz_code',
            classes: ['cursor-pointer'],
            headClasses: ['align-baseline', 'head-kmsz_code'],
            roles: [],
          },
          {
            name: 'Наименование ЛМСЗ',
            slug: 'lmsp_name',
            classes: ['cursor-pointer'],
            headClasses: ['align-baseline', 'head-lmsp_name'],
            roles: [],
          },
          {
            name: 'Статус',
            slug: 'status',
            classes: ['cursor-pointer'],
            headClasses: ['align-baseline', 'head-status'],
            roles: [],
          },
          {
            name: 'Поставщик',
            slug: 'provider_code',
            classes: ['cursor-pointer'],
            headClasses: ['align-baseline', 'head-provider_code'],
            roles: ['head_plus'],
          },
          {
            name: 'Регион',
            slug: 'provider_region',
            classes: ['cursor-pointer'],
            headClasses: ['align-baseline', 'head-provider_region'],
            roles: ['head_plus'],
          },
          {
            name: 'Требуется код ФРГУ',
            slug: 'need_frgu',
            classes: ['cursor-pointer'],
            headClasses: ['align-baseline', 'head-need_frgu'],
            roles: [],
          },
        ],
      };
    },

    computed: {
      ...mapGetters('Auth', ['roles']),
      ...mapState('LMSPRequestList', ['requests', 'pagination']),

      tableColumns() {
        let result = [];
        for (let i in this.columns) {
          if (this.columns[i].roles.length <= 0) {
            result.push(this.columns[i]);
            continue;
          }

          if (intersection(this.columns[i].roles, this.roles).length > 0) {
            result.push(this.columns[i]);
          }
        }

        return result;
      },
    },

    created() {
      this.isLoading = true;

      Client.getLMSPFilter(this.getQueryFilter()).then((response) => {
        this.bindFilter(response.data);
      });

      this.$store
        .dispatch('LMSPRequestList/load', this.getQueryFilter())
        .finally(() => {
          this.isLoading = false;
        });
    },

    destroyed() {
      this.$store.dispatch('LMSPRequestList/clear');
    },

    beforeRouteUpdate(to, from, next) {
      this.isLoading = true;

      this.$store
        .dispatch('LMSPRequestList/load', this.getQueryFilter(to.query))
        .finally(() => {
          this.isLoading = false;
        });

      next();
    },

    methods: {
      loadLMSPRequestStatuses() {
        return Client.getLMSPReqStatuses().then((response) => {
          return response.data;
        });
      },

      loadLifeSituationsSelect() {
        return Client.getLifeSituations().then((response) => {
          return response.data;
        });
      },

      loadLevels() {
        return Client.getLevels().then((response) => {
          return response.data;
        });
      },

      applyFilter(event) {
        if (event.key === 'Enter' || event.keyCode === 13) {
          this.changeFilter();
        }
      },

      bindFilter(data) {
        this.filter = {
          id: data.id,
          level: data.level,
          status: data.status,
          kmszCode: data.kmsz_code,
          lmspName: data.lmsp_name,
          needFrgu: data.need_frgu,
          providerCode: data.provider_code,
          providerRegion: data.provider_region,
          lifeSituation: data.life_situation,
        };
      },

      changeFilter() {
        let curQuery = this.$route.query;
        let newQuery = Object.assign({}, curQuery, {
          id: this.filter.id || null,
          page: 1,
          kmsz_code: this.filter.kmszCode || null,
          lmsp_name: this.filter.lmspName || null,
          need_frgu: this.filter.needFrgu || null,
          level_id: this.filter.level?.id || null,
          status_id: this.filter.status?.id || null,
          provider_code: this.filter.providerCode || null,
          provider_region: this.filter.providerRegion || null,
          life_situation_id: this.filter.lifeSituation?.id || null,
        });

        if (!isEqual(curQuery, newQuery)) {
          this.$router.push({
            query: newQuery,
          });
        }
      },

      getQueryFilter(query) {
        query = query || this.$route.query;
        return {
          id: query.id || null,
          page: query.page || 1,
          kmsz_code: query.kmsz_code || null,
          lmsp_name: query.lmsp_name || null,
          need_frgu: query.need_frgu || null,
          level_id: query.level_id || null,
          status_id: query.status_id || null,
          provider_code: query.provider_code || null,
          provider_region: query.provider_region || null,
          life_situation_id: query.life_situation_id || null,
        };
      },

      onPaginate(page) {
        this.$router.push({
          name: 'registry',
          query: Object.assign(this.getQueryFilter(), {
            page: page,
          }),
        });
      },

      onRowClick(item) {
        const isTextHighlighting =
          window.getSelection().toString().trim() !== '';

        if (!isTextHighlighting) {
          this.$router.push(this.detailLmspRoute(item.id));
        }
      },

      detailLmspRoute(id) {
        return {
          name: 'registry.detail',
          params: {
            id: id,
          },
          query: this.$route.query,
        };
      },
    },
  };
</script>

<style lang="scss" scoped>
  ::v-deep .head {
    &-id {
      min-width: rem(100px);
    }
    &-life_situation {
      min-width: rem(200px);
    }
    &-npa_level {
      min-width: rem(200px);
    }
    &-kmsz_code {
      min-width: rem(150px);
    }
    &-lmsp_name {
      min-width: rem(200px);
    }
    &-status {
      min-width: rem(200px);
    }
    &-provider_code {
      min-width: rem(200px);
    }
    &-provider_region {
      min-width: rem(200px);
    }
    &-need_frgu {
      min-width: rem(160px);
    }
  }
  .small-select {
    min-width: rem(160px);
  }
  .filter-select {
    max-width: rem(200px);
    ::v-deep .vs__selected-options {
      overflow: hidden;
      white-space: nowrap;
    }
  }
</style>
